<template>
  <div class="page-wrapper">
    <!-- ====================== PC 端 ====================== -->
    <div class="pc-version">
      <div
        class="rank-table-container"
        v-loading="loading"
        element-loading-text="拼命加载中..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(0, 0, 0, 0.3)"
      >
        <!-- 顶部搜索栏（折叠版） -->
        <div class="search-bar">
          <!-- 折叠/展开按钮 -->
          <el-button
            icon="el-icon-arrow-down"
            type="text"
            @click="toggleSearch"
            style="margin-right: 10px"
          >
            {{ searchCollapsed ? "展开搜索" : "收起搜索" }}
          </el-button>

          <!-- 搜索项 -->
          <div
            :style="{
              display: searchCollapsed ? 'none' : 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }"
          >
            <el-input
              v-model="searchParams.user_id"
              placeholder="请输入用户ID"
              clearable
              style="width: 180px"
            ></el-input>
          </div>

          <!-- 搜索/重置/列筛选 -->
          <div style="display: flex; gap: 10px; margin-left: 10px">
            <el-button
              type="primary"
              icon="el-icon-search"
              @click="getFirstSuperHumanList"
            >
              搜索
            </el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">
              重置
            </el-button>
            <el-popover
              placement="bottom"
              title="显示列筛选"
              trigger="click"
              width="240"
            >
              <el-checkbox-group v-model="checkedColumns">
                <el-checkbox
                  v-for="item in columnOptions"
                  :key="item.prop"
                  :label="item.prop"
                >{{ item.label }}</el-checkbox>
              </el-checkbox-group>
              <div style="text-align: right; margin-top: 8px">
                <el-button size="mini" @click="handleCheckAll">全选</el-button>
                <el-button size="mini" @click="handleClearAll">清空</el-button>
              </div>
              <el-button slot="reference" icon="el-icon-s-tools" type="info"
                >列筛选</el-button
              >
            </el-popover>
          </div>
        </div>

        <!-- 重点：绑定动态高度 tableHeight -->
        <el-table
          :data="tableData"
          border
          stripe
          style="width: 100%; margin-top: 20px"
          :height="tableHeight"
        >
          <el-table-column
            label="序号"
            width="80"
            align="center"
            v-if="checkedColumns.includes('index')"
          >
            <template slot-scope="scope">
              {{ (searchParams.page - 1) * searchParams.page_size + scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column
            label="头像"
            width="80"
            align="center"
            v-if="checkedColumns.includes('avatar')"
          >
            <template slot-scope="scope">
              <el-avatar :src="scope.row.avatar" size="40"></el-avatar>
            </template>
          </el-table-column>
          <el-table-column
            label="用户ID"
            prop="user_id"
            width="120"
            align="center"
            v-if="checkedColumns.includes('user_id')"
          />
          <el-table-column
            label="用户编号"
            prop="user_no"
            min-width="160"
            v-if="checkedColumns.includes('user_no')"
          />
          <el-table-column
            label="用户昵称"
            prop="nickname"
            min-width="180"
            v-if="checkedColumns.includes('nickname')"
          />
          <el-table-column
            label="盲盒名称"
            prop="box_name"
            min-width="140"
            v-if="checkedColumns.includes('box_name')"
          />
          <el-table-column
            label="超神奖品图"
            width="120"
            align="center"
            v-if="checkedColumns.includes('box_prize_image')"
          >
            <template slot-scope="scope">
              <el-image
                :src="scope.row.box_prize_image"
                style="width:60px;height:60px;border-radius:4px"
                fit="cover"
                preview
              />
            </template>
          </el-table-column>
          <el-table-column
            label="超神奖品名称"
            prop="box_prize_name"
            min-width="260"
            v-if="checkedColumns.includes('box_prize_name')"
          />
          <el-table-column
            label="数量"
            prop="num"
            width="90"
            align="center"
            v-if="checkedColumns.includes('num')"
          />
          <el-table-column
            label="兑换价值"
            width="130"
            align="center"
            v-if="checkedColumns.includes('recovery_price')"
          >
            <template slot-scope="scope">
              ¥{{ scope.row.recovery_price }}
            </template>
          </el-table-column>
          <el-table-column
            label="超神时间"
            prop="created_at"
            min-width="180"
            align="center"
            v-if="checkedColumns.includes('created_at')"
          />
        </el-table>

        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="searchParams.page"
          :page-size="searchParams.page_size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pageInfo.total"
          style="margin-top: 20px; text-align: right"
        ></el-pagination>
      </div>
    </div>

    <!-- ====================== 移动端 ====================== -->
    <div class="mobile-version">
      <div
        class="mobile-rank-container"
        v-loading="loading"
        element-loading-text="加载中..."
      >
        <!-- 搜索 -->
        <div class="mobile-search-group">
          <el-button
            type="text"
            icon="el-icon-arrow-down"
            @click="toggleSearch"
            style="width: 100%; text-align: left; padding-left: 0"
          >
            {{ searchCollapsed ? "展开搜索条件 ▼" : "收起搜索条件 ▲" }}
          </el-button>
          <div
            v-if="!searchCollapsed"
            style="display: flex; flex-direction: column; gap: 8px"
          >
            <el-input
              v-model="searchParams.user_id"
              placeholder="用户ID"
              clearable
              size="medium"
            />
          </div>
        </div>
        <div style="display: flex; gap: 8px; margin-bottom: 10px">
          <el-button
            type="primary"
            plain
            round
            @click="getFirstSuperHumanList"
            style="flex: 1"
          >搜索</el-button>
          <el-button plain round @click="resetSearch" style="flex: 1">重置</el-button>
        </div>

        <!-- 卡片列表 -->
        <div class="mobile-card-list">
          <div
            class="mobile-card"
            v-for="(item, index) in tableData"
            :key="item.id + '_' + index"
          >
            <div class="card-header">
              <div style="display: flex; align-items: center; gap: 10px">
                <div class="rank-tag">
                  <span class="tag-normal">
                    {{ (searchParams.page - 1) * searchParams.page_size + index + 1 }}
                  </span>
                </div>
                <el-avatar :src="item.avatar" size="50"></el-avatar>
                <div class="info">
                  <div class="nickname">{{ item.nickname || "未知用户" }}</div>
                  <div class="uid">
                    用户ID: {{ item.user_id }}
                  </div>
                  <div class="uid">用户编号：{{ item.user_no }}</div>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div style="display:flex;gap:10px;margin-bottom:8px">
                <el-image
                  :src="item.box_prize_image"
                  style="width:80px;height:80px;border-radius:6px"
                  fit="cover"
                  preview
                />
                <div style="flex:1">
                  <div><b>盲盒：</b>{{ item.box_name }}</div>
                  <div style="margin-top:4px;color:#333">奖品：{{ item.box_prize_name }}</div>
                </div>
              </div>
              <div>数量：{{ item.num }}</div>
              <div>兑换价值：¥{{ item.recovery_price }}</div>
              <div style="margin-top:4px;color:#999;font-size:12px">超神时间：{{ item.created_at }}</div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div style="text-align: center; margin-top: 15px">
          <el-pagination
            @current-change="handleCurrentChange"
            :current-page="searchParams.page"
            layout="total, prev, pager, next"
            :total="pageInfo.total"
            pager-count="2"
            small
          />
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import api from "../../api/api.js";
export default {
  name: "UserFirstSuperHuman",
  data() {
    return {
      loading: false,
      tableData: [],
      pageInfo: { total: 0 },

      // 搜索参数
      searchParams: {
        page: 1,
        page_size: 10,
        user_id: "",
      },

      // 列筛选配置
      columnOptions: [
        { label: "序号", prop: "index" },
        { label: "头像", prop: "avatar" },
        { label: "用户ID", prop: "user_id" },
        { label: "用户编号", prop: "user_no" },
        { label: "用户昵称", prop: "nickname" },
        { label: "盲盒名称", prop: "box_name" },
        { label: "奖品图片", prop: "box_prize_image" },
        { label: "奖品名称", prop: "box_prize_name" },
        { label: "数量", prop: "num" },
        { label: "兑换价值", prop: "recovery_price" },
        { label: "超神时间", prop: "created_at" },
      ],
      checkedColumns: [
        "index",
        "avatar",
        "user_id",
        "user_no",
        "nickname",
        "box_name",
        "box_prize_image",
        "box_prize_name",
        "num",
        "recovery_price",
        "created_at",
      ],

      searchCollapsed: true,
      tableHeight: 950, // 表格动态高度
    };
  },
  created() {
    this.getFirstSuperHumanList();
  },
  mounted() {
    this.calcTableHeight();
    // 窗口大小变化重新计算高度
    window.addEventListener('resize', this.calcTableHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight);
  },
  methods: {
    // 计算表格高度，实现表头固定
    calcTableHeight() {
      // 可视区域高度，减去搜索栏、分页、边距，按需调整差值
      const offset = 280;
      this.tableHeight = window.innerHeight - offset;
      if(this.tableHeight < 250) this.tableHeight = 250;
    },

    // 获取首个超神列表
    async getFirstSuperHumanList() {
      this.loading = true;
      try {
        const res = await api.users.getUserFirstSuperHuman({
          params: this.searchParams,
        });
        if (res.data.code == '1000') {
          this.tableData = res.data.data.data || [];
          this.pageInfo.total = res.data.data.total || 0;
        }
      } finally {
        this.loading = false;
      }
    },

    // 重置搜索条件
    resetSearch() {
      this.searchParams = {
        page: 1,
        page_size: 10,
        user_id: "",
      };
      this.getFirstSuperHumanList();
    },

    // 每页条数切换
    handleSizeChange(val) {
      this.searchParams.page_size = val;
      this.getFirstSuperHumanList();
    },
    // 页码切换
    handleCurrentChange(val) {
      this.searchParams.page = val;
      this.getFirstSuperHumanList();
    },

    // 列筛选全选/清空
    handleCheckAll() {
      this.checkedColumns = this.columnOptions.map((i) => i.prop);
    },
    handleClearAll() {
      this.checkedColumns = [];
    },

    // 折叠搜索栏
    toggleSearch() {
      this.searchCollapsed = !this.searchCollapsed;
      // 折叠展开搜索栏后重新算表格高度
      this.$nextTick(()=>{
        this.calcTableHeight();
      })
    },
  },
};
</script>


<style scoped>
/* PC样式 */
.rank-table-container {
  padding: 20px;
}
.search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* 移动端样式 */
.mobile-rank-container {
  padding: 12px;
}
.mobile-search-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}
.mobile-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mobile-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.card-header {
  margin-bottom: 10px;
}
.rank-tag {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: bold;
  font-size: 14px;
}
.tag-normal {
  width: 32px;
  height: 32px;
  background: #eee;
  color: #666;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.info .nickname {
  font-size: 16px;
  font-weight: 500;
}
.info .uid {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
.card-body {
  font-size: 13px;
  color: #666;
  line-height: 1.7;
}
</style>


<style>
@media (max-width: 768px) {
  .pc-version {
    display: none !important;
  }
  .mobile-version {
    display: block !important;
  }
}
@media (min-width: 769px) {
  .pc-version {
    display: block !important;
  }
  .mobile-version {
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/loss/superHuman.vue